/**
 * Blocking owner PAGE for choosing the business-wide frontend destination.
 *
 * Was a 560px centred modal. A modal frames this as an interruption to dismiss,
 * and it isn't one — it is the only thing this pro can do, the choice is
 * business-wide, and it decides which product they use for the season. It also
 * meant the copy had to fit a card: the sunset date and the reassurance about
 * their data were squeezed into a footnote under two stacked buttons. Full page,
 * same content, room to read it.
 *
 * Still blocking and still un-dismissable — a fixed surface above everything
 * rather than a route, so no legacy router state has to change and every entry
 * point (deep link, bookmark, refresh) lands here identically.
 */
import { Button, Text } from "@mantine/core";
import {
  IconArrowsExchange,
  IconCalendar,
  IconFileText,
  IconPalette,
  IconWand,
} from "@tabler/icons-react";
import type { ReactNode } from "react";
import { useState } from "react";

import { useAuthStore } from "@utils/stores/authStore";

import {
  postFrontendModeChoice,
  FrontendMigrationContractRequiredError,
} from "./frontendMigrationApi";
import {
  MIGRATION_CALLOUT_COPY,
  MIGRATION_FOOTER_COPY,
  MIGRATION_MODAL_GRADIENT_COLORS,
  MIGRATION_MODAL_GRADIENT_ID,
} from "./frontendMigrationModalTheme";
import {
  getIntakeMigrationDestination,
  getIntakeMigrationPrerequisiteDestination,
  isOwnerIntakeMigrationEligible,
} from "@/utils/frontendMigration";

import "./FrontendMigrationChoiceModal.scss";

const CONTRACT_REQUIRED_MESSAGE =
  "Finish setting up your plan on this page first — switching to Wizzy 2.5 needs an active contract.";

interface FrontendMigrationChoiceModalProps {
  opened: boolean;
}

interface MigrationFeatureProps {
  icon: ReactNode;
  iconTone: "blue" | "purple";
  title: string;
  description: string;
}

/** Shared SVG gradient used by feature icon strokes. */
function MigrationModalGradientDefs() {
  return (
    <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden>
      <defs>
        <linearGradient
          id={MIGRATION_MODAL_GRADIENT_ID}
          x1="0"
          y1="0"
          x2="1"
          y2="0"
        >
          <stop offset="0%" stopColor={MIGRATION_MODAL_GRADIENT_COLORS.from} />
          <stop offset="100%" stopColor={MIGRATION_MODAL_GRADIENT_COLORS.to} />
        </linearGradient>
      </defs>
    </svg>
  );
}

/** Renders one Wizzy 2.5 benefit row in the migration modal. */
function MigrationFeature({
  icon,
  iconTone,
  title,
  description,
}: MigrationFeatureProps) {
  return (
    <div className="frontend-migration-modal__feature">
      <div
        className={`frontend-migration-modal__feature-icon frontend-migration-modal__feature-icon--${iconTone}`}
      >
        {icon}
      </div>
      <div>
        <div className="frontend-migration-modal__feature-title">{title}</div>
        <div className="frontend-migration-modal__feature-copy">
          {description}
        </div>
      </div>
    </div>
  );
}

export function FrontendMigrationChoiceModal({
  opened,
}: FrontendMigrationChoiceModalProps) {
  const user = useAuthStore((state) => state.user);
  const billingContext = useAuthStore((state) => state.billingContext);
  const refreshAuth = useAuthStore((state) => state.refreshAuth);
  const updateFrontendMode = useAuthStore((state) => state.updateFrontendMode);
  const [loadingChoice, setLoadingChoice] = useState<
    "legacy" | "intake" | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const isSubmitting = loadingChoice !== null;

  if (!user) {
    return null;
  }

  const handleChoice = async (mode: "legacy" | "intake") => {
    if (isSubmitting) {
      return;
    }
    if (mode === "intake" && !isOwnerIntakeMigrationEligible(user)) {
      const destination =
        getIntakeMigrationPrerequisiteDestination(billingContext);
      // Assigning the URL we are already on is a no-op the pro reads as a dead
      // button. Say what is missing instead.
      if (window.location.pathname.startsWith(destination)) {
        setError(CONTRACT_REQUIRED_MESSAGE);
        return;
      }
      window.location.assign(destination);
      return;
    }
    setLoadingChoice(mode);
    setError(null);
    let persistedMode: "legacy" | "intake" | null = null;
    try {
      persistedMode = await postFrontendModeChoice(mode);
      updateFrontendMode(persistedMode);
    } catch (choiceError) {
      if (choiceError instanceof FrontendMigrationContractRequiredError) {
        window.location.assign(
          getIntakeMigrationPrerequisiteDestination(billingContext)
        );
        return;
      }
      const message =
        choiceError instanceof Error && choiceError.message
          ? choiceError.message
          : "We couldn't save your choice. Please try again.";
      setError(message);
      setLoadingChoice(null);
      return;
    }

    try {
      await refreshAuth();
    } catch {
      // Choice already persisted; continue with the in-memory mode.
    }

    const refreshedUser = useAuthStore.getState().user ?? user;
    if (mode === "intake") {
      window.location.replace(getIntakeMigrationDestination(refreshedUser));
      return;
    }
    setLoadingChoice(null);
  };

  if (!opened) {
    return null;
  }

  return (
    <>
      <MigrationModalGradientDefs />
      <div
        className="frontend-migration-page"
        role="dialog"
        aria-modal="true"
        aria-label="Wizzy 2.5 is here"
      >
        <div className="frontend-migration-modal">
          <div
            className="frontend-migration-modal__header"
            style={
              {
                "--migration-gradient-from":
                  MIGRATION_MODAL_GRADIENT_COLORS.from,
                "--migration-gradient-to": MIGRATION_MODAL_GRADIENT_COLORS.to,
              } as React.CSSProperties
            }
          >
            <span className="frontend-migration-modal__badge">
              Now available
            </span>
            <div className="frontend-migration-modal__title">
              <span className="frontend-migration-modal__title-accent">
                Wizzy 2.5
              </span>{" "}
              is here
            </div>
            <Text component="p" className="frontend-migration-modal__subtitle">
              Same clients, same data — with a far more capable Wizzy doing the
              work alongside you.
            </Text>
          </div>

          <div className="frontend-migration-modal__features">
            <MigrationFeature
              icon={<IconWand size={19} stroke={1.8} />}
              iconTone="blue"
              title="An AI clerk runs your client intake"
              description="Wizzy collects the documents, asks the follow-ups, and hands you a complete, organized intake."
            />
            <MigrationFeature
              icon={<IconFileText size={19} stroke={1.8} />}
              iconTone="purple"
              title="More forms, more automation, less data entry"
              description="Wider form coverage and deeper automation fill the return for you — you review instead of retype."
            />
            <MigrationFeature
              icon={<IconPalette size={19} stroke={1.8} />}
              iconTone="purple"
              title="Make it your brand"
              description="White-label the whole experience — your logo, your colors, even Wizzy in your brand color."
            />
          </div>

          <div className="frontend-migration-modal__sunset">
            <IconCalendar
              size={17}
              stroke={1.8}
              className="frontend-migration-modal__sunset-icon"
            />
            <span>{MIGRATION_CALLOUT_COPY}</span>
          </div>

          {error ? (
            <Text className="frontend-migration-modal__error">{error}</Text>
          ) : null}

          <div className="frontend-migration-modal__actions">
            <Button
              fullWidth
              variant="gradient"
              gradient={MIGRATION_MODAL_GRADIENT_COLORS}
              className="frontend-migration-modal__primary-button"
              size="lg"
              fz={20}
              fw={800}
              loading={loadingChoice === "intake"}
              disabled={isSubmitting}
              onClick={() => void handleChoice("intake")}
            >
              Update to Wizzy 2.5
            </Button>
            <Button
              fullWidth
              variant="outline"
              className="frontend-migration-modal__secondary-button"
              loading={loadingChoice === "legacy"}
              disabled={isSubmitting}
              onClick={() => void handleChoice("legacy")}
            >
              Stay on the current version for now
            </Button>
            <div className="frontend-migration-modal__footer">
              <IconArrowsExchange size={14} stroke={1.8} />
              <span>{MIGRATION_FOOTER_COPY}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
